import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabaseClient';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faComments,
  faSearch,
  faSpinner,
  faExclamationTriangle,
  faArrowLeft,
  faCircle,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { Icon } from '@iconify/react';
import { format, isToday, isYesterday } from 'date-fns';
import ChatWindow from './ChatWindow';
import { ChatMessage } from '../../hooks/useChatMessages';
import Avatar from '../ui/Avatar';
import { useUserProfilesByIds, normalizeAvatarUrl } from '../../hooks/useUserProfiles';
import { motion, AnimatePresence } from 'framer-motion';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useChatMessages } from '../../hooks/useChatMessages';
import { useLocation } from 'react-router-dom';

interface ConversationSummary {
  conversation_id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  user_avatar_url?: string | null;
  last_message: string;
  last_message_at: string;
  unread_count: number;
  total_messages: number;
}

/**
 * Chat Manager for Admin/Agent - WhatsApp style design
 * Mobile: List and chat in separate screens with back button
 * Desktop: Side-by-side layout (30% list, 70% chat)
 */
export default function HomeChatManager() {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>('');
  const [selectedUserEmail, setSelectedUserEmail] = useState<string>('');
  const [showChat, setShowChat] = useState(false); // For mobile: show chat or list

  // Get current user
  const [currentUser, setCurrentUser] = useState<any>(null);
  const { data: userProfile } = useUserProfile(currentUser?.id);
  const isAdminOrAgent = userProfile?.role === 'admin' || userProfile?.role === 'agent';
  const isAgent = userProfile?.role === 'agent';

  // Get messages for selected conversation to find admin/agent avatar
  // Keep in cache even when navigating away to prevent avatar disappearing
  const { messages: selectedMessages = [] } = useChatMessages({
    conversationId: selectedConversation || '',
    enabled: !!selectedConversation,
    staleTime: 5 * 60 * 1000, // 5 minutes - keep fresh longer
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache longer
    refetchOnWindowFocus: false, // Don't refetch when navigating back
    refetchOnMount: false, // Don't refetch on mount if data is fresh
  });

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);
    };
    fetchUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setCurrentUser(session?.user ?? null);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Helper function to add timeout to queries
  const withTimeout = async <T,>(
    promise: Promise<T>,
    timeoutMs: number = 20000,
    errorMessage: string = 'Request timeout'
  ): Promise<T> => {
    const timeout = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`${errorMessage} (${timeoutMs}ms)`));
      }, timeoutMs);
    });
    return Promise.race([promise, timeout]);
  };

  // Helper to check if error is network-related
  const isNetworkError = (error: any): boolean => {
    if (!error) return false;
    const errorMessage = error.message || error.toString() || '';
    const errorCode = error.code || '';
    
    return (
      errorMessage.includes('ERR_QUIC_PROTOCOL_ERROR') ||
      errorMessage.includes('ERR_NETWORK') ||
      errorMessage.includes('ERR_INTERNET_DISCONNECTED') ||
      errorMessage.includes('Failed to fetch') ||
      errorMessage.includes('NetworkError') ||
      errorMessage.includes('timeout') ||
      errorCode === 'PGRST301' ||
      errorCode === 'PGRST302'
    );
  };

  // Fetch all conversations (only for admin/agent)
  const { data: conversations = [], isLoading, error, refetch } = useQuery({
    queryKey: ['adminChatConversations'],
    queryFn: async (): Promise<ConversationSummary[]> => {
      if (!isAdminOrAgent) return [];

      try {
        const queryBuilder = supabase
          .from('chat_messages')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(1000);

        const queryPromise = queryBuilder as unknown as Promise<{ data: ChatMessage[] | null; error: any }>;

        const result = await withTimeout<{ data: ChatMessage[] | null; error: any }>(
          queryPromise,
          20000, // 20 second timeout for conversations list
          'Loading conversations timed out'
        );

        const { data: messages, error: messagesError } = result;

        if (messagesError) {
          if (isNetworkError(messagesError)) {
            throw new Error('Connection issue. Please check your internet connection and try again.');
          }
          throw messagesError;
        }

      // Group by conversation_id
      const conversationMap = new Map<string, {
        conversation_id: string;
        messages: ChatMessage[];
        unread: number;
      }>();

      (messages || []).forEach((msg: ChatMessage) => {
        const convId = msg.conversation_id;
        if (!conversationMap.has(convId)) {
          conversationMap.set(convId, {
            conversation_id: convId,
            messages: [],
            unread: 0,
          });
        }
        const conv = conversationMap.get(convId)!;
        conv.messages.push(msg);
        // Count unread: messages from users/carriers OR from other admin/agent (for admin-agent communication)
        if (!msg.is_read) {
          if (msg.sender_role !== 'admin' && msg.sender_role !== 'agent') {
            // Unread from user/carrier
            conv.unread++;
          } else if (msg.sender_id !== currentUser?.id) {
            // Unread from other admin/agent (for admin-agent communication)
            conv.unread++;
          }
        }
      });

      // Get user info for each conversation
      const conversationEntries = Array.from(conversationMap.entries());
      const profilePromises = conversationEntries.map(async ([convId, conv]) => {
        const latestMsg = conv.messages[0];
        let userName = 'User';
        let userEmail = 'N/A';
        let userId = convId;
        let userAvatar: string | null = null;

        // For admin-agent conversation, get info from the other admin/agent's messages
        if (convId === 'admin-agent') {
          // Get the most recent message from the other admin/agent (not current user)
          const otherAdminAgentMsgs = conv.messages
            .filter((msg) => (msg.sender_role === 'admin' || msg.sender_role === 'agent') && msg.sender_id !== currentUser?.id)
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

          if (otherAdminAgentMsgs.length > 0) {
            const otherAdminAgentMsg = otherAdminAgentMsgs[0];
            userName = otherAdminAgentMsg.sender_name || userName;
            userEmail = otherAdminAgentMsg.sender_email || userEmail;
            userId = otherAdminAgentMsg.sender_id;
            // Use sender_avatar_url from message (already normalized in DB) - this is the key!
            userAvatar = otherAdminAgentMsg.sender_avatar_url || null;
          }
        } else {
          // For regular user conversations, get info from user messages
          // Get the most recent message from user/carrier (not admin/agent)
          const userMessages = conv.messages
            .filter((msg) => msg.sender_role !== 'admin' && msg.sender_role !== 'agent')
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

          if (userMessages.length > 0) {
            const userMessage = userMessages[0];
            userName = userMessage.sender_name || userName;
            userEmail = userMessage.sender_email || userEmail;
            userId = userMessage.sender_id;
            // Use sender_avatar_url from message (already normalized in DB) - this prevents avatar disappearing!
            userAvatar = userMessage.sender_avatar_url || null;
          }

          // If still N/A, try to use latest message sender info even if it's admin (edge case)
          if (userEmail === 'N/A' && userMessages.length === 0) {
            userEmail = latestMsg.sender_email || 'N/A';
            userName = latestMsg.sender_name || 'User';
            userId = latestMsg.sender_id;
            // Also try to get avatar from latest message if available (already normalized)
            if (latestMsg.sender_avatar_url) {
              userAvatar = latestMsg.sender_avatar_url;
            }
          }
        }

        return {
          conversation_id: convId,
          user_id: userId,
          user_name: userName,
          user_email: userEmail,
          user_avatar_url: userAvatar,
          last_message: latestMsg.message,
          last_message_at: latestMsg.created_at,
          unread_count: conv.unread,
          total_messages: conv.messages.length,
        };
      });

      const allConversations = await Promise.all(profilePromises);

      // Add default admin-agent conversation if it doesn't exist and user is admin/agent
      // This allows admin and agent to communicate with each other
      const hasAdminAgentConv = allConversations.some(conv => conv.conversation_id === 'admin-agent');
      if (!hasAdminAgentConv && isAdminOrAgent) {
        // Get the other admin/agent to show in the conversation
        // If current user is agent, show admin; if admin, show agent
        const targetRole = isAgent ? 'admin' : 'agent';
        const { data: otherAdminAgents } = await supabase
          .from('profiles')
          .select('id, full_name, email, role, avatar_url')
          .eq('role', targetRole)
          .limit(1);

        if (otherAdminAgents && otherAdminAgents.length > 0) {
          const other = otherAdminAgents[0];
          // Normalize avatar URL using the same function as elsewhere
          const normalizedAvatarUrl = other.avatar_url
            ? (other.avatar_url.startsWith('http') ? other.avatar_url : supabase.storage.from('avatars').getPublicUrl(other.avatar_url).data.publicUrl)
            : null;
          allConversations.unshift({
            conversation_id: 'admin-agent',
            user_id: other.id,
            user_name: `${other.full_name || other.email || 'Team'} (${other.role})`,
            user_email: other.email,
            user_avatar_url: normalizedAvatarUrl,
            last_message: 'Start a conversation with your team',
            last_message_at: new Date().toISOString(),
            unread_count: 0,
            total_messages: 0,
          });
        } else {
          // If no other admin/agent found, still create the conversation
          allConversations.unshift({
            conversation_id: 'admin-agent',
            user_id: currentUser?.id || 'admin-agent',
            user_name: isAgent ? 'Admin' : 'Agent',
            user_email: 'team@support.com',
            user_avatar_url: null,
            last_message: 'Start a conversation with your team',
            last_message_at: new Date().toISOString(),
            unread_count: 0,
            total_messages: 0,
          });
        }
      }

        return allConversations;
      } catch (err: any) {
        // Re-throw with better error message for network errors
        if (isNetworkError(err)) {
          const networkError = new Error(
            `Connection issue: ${err.message || 'Network error. Please check your internet connection.'}`
          );
          (networkError as any).isNetworkError = true;
          throw networkError;
        }
        throw err;
      }
    },
    enabled: isAdminOrAgent,
    staleTime: 5 * 60 * 1000, // 5 minutes - conversaciones no cambian tan frecuentemente
    gcTime: 30 * 60 * 1000, // 30 minutes - mantener en cache
    refetchInterval: false, // Don't auto-refetch - use cache and realtime updates
    refetchOnWindowFocus: false, // Don't refetch on window focus - use cache
    refetchOnMount: false, // Don't refetch on mount if data is fresh
    retry: (failureCount, error: any) => {
      // Retry up to 3 times for network errors, 1 time for other errors
      if (isNetworkError(error)) {
        return failureCount < 3;
      }
      return failureCount < 1;
    },
    retryDelay: (attemptIndex) => {
      // Exponential backoff: 1s, 2s, 4s
      return Math.min(1000 * Math.pow(2, attemptIndex), 4000);
    },
  });

  // Get unique user IDs from conversations
  const userIds = useMemo(() => {
    return Array.from(new Set(conversations.map((conv) => conv.user_id)));
  }, [conversations]);

  // Fetch user profiles for all users in conversations
  const { data: userProfilesMap = new Map() } = useUserProfilesByIds(userIds);

  // Filter conversations by search
  const filteredConversations = useMemo(() => {
    if (!searchQuery) return conversations;
    const query = searchQuery.toLowerCase();
    return conversations.filter((conv) =>
      conv.user_name.toLowerCase().includes(query) ||
      conv.user_email.toLowerCase().includes(query) ||
      conv.last_message.toLowerCase().includes(query)
    );
  }, [conversations, searchQuery]);

  // Sort conversations by last message time
  const sortedConversations = useMemo(() => {
    return [...filteredConversations].sort((a, b) =>
      new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime()
    );
  }, [filteredConversations]);

  // Handle conversation selection
  const handleSelectConversation = (conv: ConversationSummary) => {
    setSelectedConversation(conv.conversation_id);
    setSelectedUserName(conv.user_name);
    setSelectedUserEmail(conv.user_email);
    if (window.innerWidth < 1024) {
      setShowChat(true);
    }
  };

  // Handle creating admin-agent conversation
  const handleCreateAdminAgentChat = () => {
    setSelectedConversation('admin-agent');
    setSelectedUserName('Admin/Agent Chat');
    setSelectedUserEmail('team@support.com');
    if (window.innerWidth < 1024) {
      setShowChat(true);
    }
  };

  const handleBackToList = () => {
    setShowChat(false);
    setSelectedConversation(null);
  };

  // Format time for conversation list (WhatsApp style)
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    if (isToday(date)) {
      return format(date, 'HH:mm');
    } else if (isYesterday(date)) {
      return 'Yesterday';
    } else {
      return format(date, 'MMM d');
    }
  };

  // Prevent body scroll on mobile when chat is open
  useEffect(() => {
    if (isOpen && window.innerWidth < 768) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Show this component only on Home and only for admin/agent.
  if (!isHome || !isAdminOrAgent) {
    return null;
  }

  // Calculate total unread count
  const totalUnread = conversations.reduce((sum, conv) => sum + conv.unread_count, 0);

  return (
    <>
      {/* Floating Chat Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="
          fixed
          bottom-32
          sm:bottom-32
          right-4
          sm:right-6
          w-14 h-14
          bg-blue-600 hover:bg-blue-700 active:bg-blue-800
          text-white
          rounded-full
          shadow-xl hover:shadow-2xl
          z-[40]
          flex items-center justify-center
          transition-all duration-300
          touch-manipulation
          ring-4 ring-blue-500/20 hover:ring-blue-500/40
        "
        whileHover={{ scale: 1.05, boxShadow: '0 20px 25px -5px rgba(59, 130, 246, 0.4)' }}
        whileTap={{ scale: 0.95 }}
        aria-label="Open chat manager"
        animate={{
          scale: isOpen ? 0 : 1,
          opacity: isOpen ? 0 : 1
        }}
        style={{ pointerEvents: isOpen ? 'none' : 'auto' }}
      >
        <Icon icon="mdi:comment-text-outline" className="w-6 h-6" />
        {totalUnread > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center border-2 border-white">
            {totalUnread > 9 ? '9+' : totalUnread}
          </span>
        )}
      </motion.button>

      {/* Chat Manager Window - WhatsApp Style */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="
              fixed
              top-0
              left-0
              right-0
              bottom-0
              sm:top-auto sm:left-auto sm:right-6 sm:bottom-32
              sm:w-[600px] sm:h-[600px] sm:max-h-[calc(100vh-140px)]
              lg:w-[800px] lg:h-[650px] lg:max-h-[calc(100vh-140px)]
              z-[100]
              shadow-2xl
              sm:rounded-lg
              overflow-hidden
              border-0 sm:border border-gray-200
              bg-white
              flex flex-col
            "
          >
            {/* Mobile: Show list or chat */}
            <div className="lg:hidden flex-1 flex flex-col h-full">
              <AnimatePresence mode="wait">
                {showChat && selectedConversation ? (
                  /* Mobile Chat View - WhatsApp Style */
                  <motion.div
                    key="chat"
                    initial={{ x: '100%' }}
                    animate={{ x: 0 }}
                    exit={{ x: '100%' }}
                    transition={{ type: 'tween', duration: 0.3 }}
                    className="flex flex-col h-full bg-white"
                  >
                    {/* Header with Back Button */}
                    <div className="flex items-center gap-3 p-3 border-b border-gray-200 bg-white flex-shrink-0">
                      <button
                        onClick={handleBackToList}
                        className="text-gray-600 hover:text-gray-900 transition-all duration-200 p-1.5 rounded-lg hover:bg-gray-100 hover:shadow-md"
                        aria-label="Back to conversations"
                      >
                        <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
                      </button>
                      {(() => {
                        const selectedConv = conversations.find(c => c.conversation_id === selectedConversation);
                        const userProfile = selectedConv ? userProfilesMap.get(selectedConv.user_id) : null;

                        // Determine avatar: for admin-agent conversation, get from messages; otherwise use user avatar
                        let avatarUrl: string | null = null;
                        let displayName = selectedUserName;
                        let displayEmail = selectedUserEmail !== 'N/A' ? selectedUserEmail : 'N/A';

                        // ALWAYS prioritize sender_avatar_url from messages (already normalized in DB)
                        // This prevents avatars from disappearing when cache is cleared
                        if (selectedMessages.length > 0) {
                          // For admin-agent, get from other admin/agent's messages
                          if (selectedConversation === 'admin-agent') {
                            const otherAdminAgentMsg = selectedMessages
                              .filter(msg => (msg.sender_role === 'admin' || msg.sender_role === 'agent') && msg.sender_id !== currentUser?.id)
                              .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
                            if (otherAdminAgentMsg?.sender_avatar_url) {
                              avatarUrl = otherAdminAgentMsg.sender_avatar_url;
                              displayName = otherAdminAgentMsg.sender_name;
                              displayEmail = otherAdminAgentMsg.sender_email;
                            }
                          } else {
                            // For user conversations, get from user's messages
                            const userMsg = selectedMessages
                              .filter(msg => msg.sender_role !== 'admin' && msg.sender_role !== 'agent')
                              .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
                            if (userMsg?.sender_avatar_url) {
                              avatarUrl = userMsg.sender_avatar_url;
                              displayName = userMsg.sender_name;
                              displayEmail = userMsg.sender_email;
                            }
                          }
                        }

                        // Fallback to conversation data or profile (only if no message avatar found)
                        if (!avatarUrl) {
                          if (selectedConversation === 'admin-agent') {
                            avatarUrl = selectedConv?.user_avatar_url || (userProfile?.avatar_url ? normalizeAvatarUrl(userProfile.avatar_url) : null);
                            displayName = userProfile?.full_name || selectedUserName;
                            displayEmail = userProfile?.email || displayEmail;
                          } else {
                            avatarUrl = selectedConv?.user_avatar_url || (userProfile?.avatar_url ? normalizeAvatarUrl(userProfile.avatar_url) : null);
                            displayName = userProfile?.full_name || selectedUserName;
                            displayEmail = selectedUserEmail !== 'N/A' ? selectedUserEmail : (userProfile?.email || 'N/A');
                          }
                        }

                        return (
                          <>
                            <Avatar
                              src={avatarUrl}
                              name={displayName}
                              email={displayEmail}
                              size="sm"
                            />
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-gray-900 text-sm truncate">
                                {displayName}
                              </h3>
                              <p className="text-xs text-gray-500 truncate">{displayEmail}</p>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    <div className="flex-1 min-h-0 overflow-hidden">
                      <ChatWindow
                        conversationId={selectedConversation}
                        conversationTitle={selectedUserName}
                        className="h-full border-0 rounded-none"
                      />
                    </div>
                  </motion.div>
                ) : (
                  /* Mobile List View - WhatsApp Style */
                  <motion.div
                    key="list"
                    initial={{ x: '-100%' }}
                    animate={{ x: 0 }}
                    exit={{ x: '-100%' }}
                    transition={{ type: 'tween', duration: 0.3 }}
                    className="flex flex-col h-full"
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-white flex-shrink-0">
                      <h3 className="font-semibold text-gray-900 text-base">Chats</h3>
                      <button
                        onClick={() => setIsOpen(false)}
                        className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                        aria-label="Close chat"
                      >
                        <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Search */}
                    <div className="p-2 border-b border-gray-200 bg-white flex-shrink-0">
                      <div className="relative">
                        <FontAwesomeIcon
                          icon={faSearch}
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
                        />
                        <input
                          type="text"
                          placeholder="Search or start new chat"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 bg-gray-100 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white text-sm"
                        />
                      </div>
                    </div>

                    {/* Conversations List */}
                    <div className="flex-1 overflow-y-auto" style={{ flex: '1 1 0%', overflowY: 'auto' }}>
                      {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-blue-600 animate-spin" />
                        </div>
                      ) : error ? (
                        <div className="flex flex-col items-center justify-center py-8 text-center px-4">
                          <FontAwesomeIcon icon={faExclamationTriangle} className="w-12 h-12 text-red-300 mb-3" />
                          <p className="text-red-600 font-medium mb-2">
                            {error instanceof Error && (error as any).isNetworkError
                              ? 'Connection issue'
                              : 'Error loading conversations'}
                          </p>
                          <p className="text-sm text-gray-500 mb-4">
                            {error instanceof Error && (error as any).isNetworkError
                              ? 'Please check your internet connection. The app will retry automatically.'
                              : error instanceof Error
                              ? error.message
                              : 'An unexpected error occurred'}
                          </p>
                          <button
                            onClick={() => refetch()}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 text-sm shadow-md hover:shadow-lg hover:shadow-blue-500/50 hover:scale-105 active:scale-95 ring-2 ring-blue-500/20 hover:ring-blue-500/40"
                          >
                            Try Again
                          </button>
                        </div>
                      ) : sortedConversations.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center px-4">
                          <FontAwesomeIcon icon={faComments} className="w-16 h-16 text-gray-300 mb-4" />
                          <p className="text-gray-600 font-medium mb-1">
                            {searchQuery ? 'No conversations found' : 'No conversations'}
                          </p>
                          {searchQuery && (
                            <p className="text-sm text-gray-500">Try different search terms</p>
                          )}
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-200">
                          {sortedConversations.map((conv) => {
                            const userProfile = userProfilesMap.get(conv.user_id);
                            // ✅ Use conversation avatar first (from messages), then profile, then fallback
                            // This ensures avatars show immediately even if profiles aren't loaded yet
                            const avatarUrl = conv.user_avatar_url 
                              ? normalizeAvatarUrl(conv.user_avatar_url) 
                              : (userProfile?.avatar_url ? normalizeAvatarUrl(userProfile.avatar_url) : null);
                            const displayName = userProfile?.full_name || conv.user_name;
                            const displayEmail = conv.user_email !== 'N/A' ? conv.user_email : (userProfile?.email || 'N/A');

                            return (
                              <button
                                key={conv.conversation_id}
                                onClick={() => handleSelectConversation(conv)}
                                className="w-full p-3 text-left hover:bg-gray-50 active:bg-gray-100 transition-all duration-200 rounded-lg hover:shadow-md hover:scale-[1.02] active:scale-[0.98]"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="relative flex-shrink-0">
                                    <Avatar
                                      src={avatarUrl}
                                      name={displayName}
                                      email={displayEmail}
                                      size="md"
                                    />
                                    {conv.unread_count > 0 && (
                                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center border-2 border-white">
                                        {conv.unread_count > 9 ? '9+' : conv.unread_count}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-1">
                                      <p className="font-semibold text-gray-900 text-sm truncate">
                                        {displayName}
                                      </p>
                                      <p className="text-xs text-gray-400 flex-shrink-0 ml-2">
                                        {formatTime(conv.last_message_at)}
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <p className="text-sm text-gray-600 truncate flex-1">
                                        {conv.last_message}
                                      </p>
                                      {conv.unread_count > 0 && (
                                        <span className="bg-blue-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] px-1.5 flex items-center justify-center flex-shrink-0">
                                          {conv.unread_count > 9 ? '9+' : conv.unread_count}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Desktop: Side-by-side layout (35% list, 65% chat) */}
            <div className="hidden lg:flex flex-1 min-h-0 h-full">
              {/* Conversations List - 35% */}
              <div className="w-[35%] border-r border-gray-200 flex flex-col flex-shrink-0 h-full">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white flex-shrink-0">
                  <h3 className="font-semibold text-gray-900 text-base">Chats</h3>
                  <div className="flex items-center gap-2">
                    {/* Quick action: Admin-Agent chat button */}
                    {isAdminOrAgent && (
                      <button
                        onClick={handleCreateAdminAgentChat}
                        className="text-blue-600 hover:text-blue-700 transition-all duration-200 p-1.5 rounded-lg hover:bg-blue-50 hover:shadow-md hover:scale-110 active:scale-95 ring-2 ring-blue-500/20 hover:ring-blue-500/40"
                        aria-label="Open admin-agent chat"
                        title="Chat with team"
                      >
                        <FontAwesomeIcon icon={faComments} className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => setIsOpen(false)}
                      className="text-gray-400 hover:text-gray-600 transition-all duration-200 p-1.5 rounded-lg hover:bg-gray-100 hover:shadow-md"
                      aria-label="Close chat"
                    >
                      <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Search */}
                <div className="p-3 border-b border-gray-200 bg-white flex-shrink-0">
                  <div className="relative">
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Search or start new chat"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-gray-100 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white text-sm"
                    />
                  </div>
                </div>

                {/* Conversations List */}
                <div className="flex-1 overflow-y-auto" style={{ flex: '1 1 0%', overflowY: 'auto' }}>
                  {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-blue-600 animate-spin" />
                    </div>
                  ) : sortedConversations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center px-4 relative overflow-hidden">
                      {/* Background illustration */}
                      <div className="absolute inset-0 opacity-5 pointer-events-none">
                        <img
                          src="/core/cyan-blur.png"
                          alt=""
                          className="w-full h-full object-cover"
                          style={{ transform: 'scale(1.5)' }}
                        />
                      </div>
                      <div className="relative z-10">
                        <img
                          src="/core/placeholder.svg"
                          alt="No conversations"
                          className="w-24 h-24 mx-auto mb-3 opacity-30"
                        />
                        <p className="text-gray-600 font-medium mb-1">
                          {searchQuery ? 'No conversations found' : 'No conversations'}
                        </p>
                        {searchQuery && (
                          <p className="text-sm text-gray-500">Try different search terms</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {sortedConversations.map((conv) => {
                        const userProfile = userProfilesMap.get(conv.user_id);
                        // ✅ Use conversation avatar first (from messages), then profile, then fallback
                        // This ensures avatars show immediately even if profiles aren't loaded yet
                        const avatarUrl = conv.user_avatar_url 
                          ? normalizeAvatarUrl(conv.user_avatar_url) 
                          : (userProfile?.avatar_url ? normalizeAvatarUrl(userProfile.avatar_url) : null);
                        const isSelected = selectedConversation === conv.conversation_id;
                        const displayName = userProfile?.full_name || conv.user_name;
                        const displayEmail = conv.user_email !== 'N/A' ? conv.user_email : (userProfile?.email || 'N/A');

                        return (
                          <button
                            key={conv.conversation_id}
                            onClick={() => handleSelectConversation(conv)}
                            className={`w-full p-3 text-left hover:bg-gray-50 transition-all duration-200 rounded-lg hover:shadow-md hover:scale-[1.02] active:scale-[0.98] ${isSelected ? 'bg-gray-50 shadow-sm ring-2 ring-blue-500/20' : ''
                              }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="relative flex-shrink-0">
                                <Avatar
                                  src={avatarUrl}
                                  name={displayName}
                                  email={displayEmail}
                                  size="md"
                                />
                                {conv.unread_count > 0 && (
                                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center border-2 border-white">
                                    {conv.unread_count > 9 ? '9+' : conv.unread_count}
                                  </span>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <p className="font-semibold text-gray-900 text-sm truncate">
                                    {displayName}
                                  </p>
                                  <p className="text-xs text-gray-400 flex-shrink-0 ml-2">
                                    {formatTime(conv.last_message_at)}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <p className="text-sm text-gray-600 truncate flex-1">
                                    {conv.last_message}
                                  </p>
                                  {conv.unread_count > 0 && (
                                    <span className="bg-blue-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] px-1.5 flex items-center justify-center flex-shrink-0">
                                      {conv.unread_count > 9 ? '9+' : conv.unread_count}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Chat Window - 65% */}
              <div className="flex-1 flex flex-col min-w-0 min-h-0 h-full">
                {selectedConversation ? (
                  <div className="flex-1 h-full flex flex-col bg-white min-h-0 overflow-hidden relative">
                    {/* Background illustration */}
                    <div className="absolute inset-0 opacity-[0.03] pointer-events-none overflow-hidden z-0">
                      <img
                        src="/core/cyan-blur.png"
                        alt=""
                        className="w-full h-full object-cover"
                        style={{ transform: 'scale(1.2)' }}
                      />
                    </div>
                    <div className="relative z-10 flex flex-col h-full">
                      {/* Desktop Header */}
                      <div className="flex items-center gap-3 p-3 border-b border-gray-200 bg-white flex-shrink-0">
                        {(() => {
                          const selectedConv = conversations.find(c => c.conversation_id === selectedConversation);
                          const userProfile = selectedConv ? userProfilesMap.get(selectedConv.user_id) : null;

                          // ALWAYS prioritize sender_avatar_url from messages (already normalized in DB)
                          // This prevents avatars from disappearing when cache is cleared
                          let avatarUrl: string | null = null;
                          let displayName = selectedUserName;
                          let displayEmail = selectedUserEmail !== 'N/A' ? selectedUserEmail : 'N/A';

                          if (selectedMessages.length > 0) {
                            // For admin-agent, get from other admin/agent's messages
                            if (selectedConversation === 'admin-agent') {
                              const otherAdminAgentMsg = selectedMessages
                                .filter(msg => (msg.sender_role === 'admin' || msg.sender_role === 'agent') && msg.sender_id !== currentUser?.id)
                                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
                              if (otherAdminAgentMsg?.sender_avatar_url) {
                                avatarUrl = otherAdminAgentMsg.sender_avatar_url;
                                displayName = otherAdminAgentMsg.sender_name;
                                displayEmail = otherAdminAgentMsg.sender_email;
                              }
                            } else {
                              // For user conversations, get from user's messages
                              const userMsg = selectedMessages
                                .filter(msg => msg.sender_role !== 'admin' && msg.sender_role !== 'agent')
                                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
                              if (userMsg?.sender_avatar_url) {
                                avatarUrl = userMsg.sender_avatar_url;
                                displayName = userMsg.sender_name;
                                displayEmail = userMsg.sender_email;
                              }
                            }
                          }

                          // Fallback to conversation data or profile (only if no message avatar found)
                          if (!avatarUrl) {
                            if (selectedConversation === 'admin-agent') {
                              avatarUrl = selectedConv?.user_avatar_url || (userProfile?.avatar_url ? normalizeAvatarUrl(userProfile.avatar_url) : null);
                              displayName = userProfile?.full_name || selectedUserName;
                              displayEmail = userProfile?.email || displayEmail;
                            } else {
                              avatarUrl = selectedConv?.user_avatar_url || (userProfile?.avatar_url ? normalizeAvatarUrl(userProfile.avatar_url) : null);
                              displayName = userProfile?.full_name || selectedUserName;
                              displayEmail = selectedUserEmail !== 'N/A' ? selectedUserEmail : (userProfile?.email || 'N/A');
                            }
                          }

                          return (
                            <>
                              <Avatar
                                src={avatarUrl}
                                name={displayName}
                                email={displayEmail}
                                size="md"
                              />
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-gray-900 text-base truncate">
                                  {displayName}
                                </h3>
                                <p className="text-xs text-gray-500 truncate">{displayEmail}</p>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <FontAwesomeIcon icon={faCircle} className="w-2 h-2 text-green-500" />
                                <span>Online</span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                      <div className="flex-1 min-h-0 overflow-hidden relative z-10" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <ChatWindow
                          conversationId={selectedConversation}
                          conversationTitle={selectedUserName}
                          className="h-full border-0 rounded-none"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Show conversations list when no conversation selected (like mobile) */
                  <div className="flex-1 flex flex-col bg-white min-h-0 overflow-hidden">
                    <div className="p-4 border-b border-gray-200 bg-white flex-shrink-0">
                      <h3 className="font-semibold text-gray-900 text-base">Select a conversation</h3>
                      <p className="text-xs text-gray-500 mt-1">Choose from the list to start chatting</p>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2" style={{ flex: '1 1 0%', overflowY: 'auto' }}>
                      {sortedConversations.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center px-4 relative overflow-hidden">
                          {/* Background illustration */}
                          <div className="absolute inset-0 opacity-5 pointer-events-none">
                            <img
                              src="/core/cyan-blur.png"
                              alt=""
                              className="w-full h-full object-cover"
                              style={{ transform: 'scale(1.5)' }}
                            />
                          </div>
                          <div className="relative z-10">
                            <img
                              src="/core/placeholder.svg"
                              alt="No conversations"
                              className="w-32 h-32 mx-auto mb-4 opacity-30"
                            />
                            <p className="text-gray-600 font-medium mb-1">No conversations</p>
                            <p className="text-sm text-gray-500">Start a conversation to see it here</p>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          {sortedConversations.map((conv) => {
                            const userProfile = userProfilesMap.get(conv.user_id);
                            // Use avatar from conversation object first (which has fallback logic), then profile map
                            const avatarUrl = conv.user_avatar_url || (userProfile?.avatar_url ? normalizeAvatarUrl(userProfile.avatar_url) : null);
                            const displayName = userProfile?.full_name || conv.user_name;
                            const displayEmail = conv.user_email !== 'N/A' ? conv.user_email : (userProfile?.email || 'N/A');

                            return (
                              <button
                                key={conv.conversation_id}
                                onClick={() => handleSelectConversation(conv)}
                                className="flex flex-col items-center p-4 hover:bg-gray-50 rounded-lg transition-colors border border-gray-200"
                              >
                                <div className="relative mb-2">
                                  <Avatar
                                    src={avatarUrl}
                                    name={displayName}
                                    email={displayEmail}
                                    size="lg"
                                  />
                                  {conv.unread_count > 0 && (
                                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center border-2 border-white">
                                      {conv.unread_count > 9 ? '9+' : conv.unread_count}
                                    </span>
                                  )}
                                </div>
                                <p className="font-semibold text-gray-900 text-sm text-center truncate w-full">
                                  {displayName}
                                </p>
                                <p className="text-xs text-gray-500 text-center truncate w-full mt-1">
                                  {conv.last_message}
                                </p>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Overlay to close on click outside - Desktop only */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="
            fixed inset-0 
            bg-black bg-opacity-20 
            z-[99]
            hidden
            sm:block
          "
          />
        )}
      </AnimatePresence>
    </>
  );
}
